import {
    AbstractControl,
    ValidationErrors,
    ValidatorFn,
    Validators,
} from '@angular/forms';

export class Validations {
    /**
     * Regular Expression for Number, Letters without space
     */
    static PATTERN_NUM_NO_SPACE = /^[0-9]+$/i;

    /**
     * Check for empty (optional fields) values
     *
     * @param value
     */
    static isEmptyInputValue(value: any): boolean {
        return value == null || value.length === 0;
    }

    /**
     * Must match validator
     *
     * @param controlPath A dot-delimited string values that define the path to the control.
     * @param matchingControlPath A dot-delimited string values that define the path to the matching control.
     */
    static mustMatch(
        controlPath: string,
        matchingControlPath: string
    ): ValidatorFn {
        return (formGroup: AbstractControl): ValidationErrors | null => {
            // Get the control and matching control
            const control = formGroup.get(controlPath);
            const matchingControl = formGroup.get(matchingControlPath);

            // Return if control or matching control doesn't exist
            if (!control || !matchingControl) {
                return null;
            }

            // Delete the mustMatch error to reset the error on the matching control
            if (matchingControl.hasError('mustMatch')) {
                delete matchingControl.errors.mustMatch;
                matchingControl.updateValueAndValidity();
            }

            // Don't validate empty values on the matching control
            // Don't validate if values are matching
            if (
                this.isEmptyInputValue(matchingControl.value) ||
                control.value === matchingControl.value
            ) {
                return null;
            }

            // Prepare the validation errors
            const errors = { mustMatch: true };

            // Set the validation error on the matching control
            matchingControl.setErrors(errors);

            // Return the errors
            return errors;
        };
    }

    /**
     * Confirm email validator
     *
     * @param {AbstractControl} control
     * @returns {ValidationErrors | null}
     */
    static confirmEmail(control: AbstractControl): ValidationErrors | null {
        if (!control.parent || !control) {
            return;
        }

        const email = control.parent.get('email');
        const emailConfirm = control.parent.get('emailConfirm');

        if (!email || !emailConfirm) {
            return;
        }

        if (emailConfirm.value === '') {
            return;
        }

        if (email.value !== emailConfirm.value) {
            return {
                emailNotMatch: true,
            };
        }
    }

    /**
     * Confirm identification validator
     *
     * @param {AbstractControl} control
     * @returns {ValidationErrors | null}
     */
    static confirmIdentification(
        control: AbstractControl
    ): ValidationErrors | null {
        if (!control.parent || !control) {
            return;
        }

        const id_number = control.parent.get('id_number');
        const id_numberConfirm = control.parent.get('id_numberConfirm');

        if (!id_number || !id_numberConfirm) {
            return;
        }

        if (id_numberConfirm.value === '') {
            return;
        }

        if (id_number.value !== id_numberConfirm.value) {
            return {
                identificationsNotMatch: true,
            };
        }
    }

    /**
     * Confirm password validator
     *
     * @param {AbstractControl} control
     * @returns {ValidationErrors | null}
     */
    static confirmPassword(control: AbstractControl): ValidationErrors | null {
        if (!control.parent || !control) {
            return;
        }

        const password = control.parent.get('password');
        const passwordConfirm = control.parent.get('passwordConfirm');

        if (!password || !passwordConfirm) {
            return;
        }

        if (passwordConfirm.value === '') {
            return;
        }

        if (password.value === passwordConfirm.value) {
            return;
        }

        return { passwordsNotMatching: true };
    }

    /**
     * Validates is password has digits
     * @param control AbstractControl
     */
    static OnlyNumber(control: AbstractControl) {
        if (
            control.value &&
            !Validations.PATTERN_NUM_NO_SPACE.test(control.value)
        ) {
            return { noDigitOnPassword: true };
        }
        return null;
    }

    /**
     * Validations Email
     */
    static ForEmail = Validators.compose([
        Validators.email,
        Validators.minLength(3),
        Validators.maxLength(255),
    ]);

    /**
     * Validations Identity
     */
    static ForIdentity = Validators.compose([
        Validations.OnlyNumber,
        Validators.minLength(7),
        Validators.maxLength(20),
    ]);

    /**
     * Validations phone number
     */
    static ForPhone = Validators.compose([
        Validations.OnlyNumber,
        Validators.minLength(10),
        Validators.maxLength(10),
    ]);
}
